import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Plot from 'react-plotly.js';
import { selectRTFO } from '../../reducers/rtfoSlice';
import { selectUser } from '../../reducers/userSlice';
import { chartColors } from '../../helpers/chartColors';
import { useTheme } from '@mui/styles';
import { rtfoConversionFactors } from './rtfoConversionFactors';

export const RTFOFracModelHalfLengthProbabilityChart = () => {

  const theme = useTheme();
  const user = useSelector(selectUser);
  const darkMode = user?.value?.darkMode;
  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;
  const units = currentRTFO?.settings?.units || 'metric';


  const data = useMemo(() => {
    if (currentRTFO) {
      return getChartData();
    };

    function getChartData() {
      try {
        
        // add traces
        let traces = [];

        let stageNames = currentRTFO?.stages;

        const pumpedWells = [...new Set(stageNames?.map(stageName => stageName?.split('_')?.[1]))]?.sort();

        // Add trace for each well
        for (let i = 0; i < pumpedWells?.length; i++) {
          const wellName = pumpedWells[i];
          
          // Get all stages for a given well
          const wellStages = stageNames?.filter(stageName => stageName.includes(wellName));

          // Construct an array of frac half-lengths
          let fracLengthArray = wellStages?.map(stageName => {
            return (currentRTFO?.stageData?.[stageName]?.fracModel?.halfLength || (currentRTFO?.stageData?.[stageName]?.fracModel?.fracArea / (2 * currentRTFO?.settings?.fracHeight))) *
              (units === 'field' ? rtfoConversionFactors.halfLength : 1);
          });

          fracLengthArray = fracLengthArray?.filter(l => !isNaN(l) && l > 0)?.sort((a, b) => a - b);

          const X = fracLengthArray;

          if (X?.length === 0) continue;

          const Y = X.map((l, i) => (parseInt(i + 1) - 0.5) / X.length); 

          traces.push({ // probability trace for the well
            x: X,
            y: Y,
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x1',
            yaxis: 'y1',
            name: wellName,
            line: {
              width: 2,
              color: chartColors[parseInt(i + 1)],
            },
            marker: {
              size: 0,
              color: 'transparent'
            }
          });
        };
  
        return traces;
  
      } catch (error) {
        console.log(error);
        return [];
      };
    };
  }, [currentRTFO, units]);


  const layout = {
    yaxis: { // Probability y axis
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      title: {
        text: 'Probability',
        font: {
          size: 12
        },
      },
      anchor: 'x',
      side: 'left',
      range: [0, 1]
    },
    xaxis: { // Half length x axis
      title: {
        text: `Fracture Half Length (${units === 'field' ? 'ft' : 'm'})`,
        font: {
          size: 12
        }
      },
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      anchor: 'y',
      showspikes: false
      // spikethickness: 2,
      // spikedash: "dot",
      // spikecolor: "#999999",
      // spikemode: "across"
    },
    hovermode: "closest",
    // hoverdistance: 10, // Distance to show hover label of data point
    // spikedistance: 1000,
    autosize: true,
    showlegend: true,
    legend: {
      orientation: "h",
      yanchor: "top",
      font: {
        color: darkMode ? 'lightgrey' : 'grey',
        size: 10
      },
      y: -0.15
    },
    annotations: [
      {
        text: "Frac Half Length Probability",
          font: {
          size: 14,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: 1.135, //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      {
        text: `Pad: ${currentRTFO?.name}`,
          font: {
          size: 10,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: 1.08, //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
    ],
    shapes: [
      
    ],
    uirevision: true,
    margin: {"t": 60, "l": 60, "r": 60, "b": 50},
    images: [
      {
        x: 0.055,
        y: 1.05,
        sizex: 0.085,
        sizey: 0.1,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xref: "page",
        yref: "page",
        xanchor: "right",
        yanchor: "bottom",
        opacity: 0.75
      }
    ],
    plot_bgcolor: "transparent",
    paper_bgcolor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]
  };

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: `${currentRTFO?.name}_Frac model probability_${new Date().toISOString().split('T')[0]}`,
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };


  return (
    <Plot
      data={data}
      layout={layout}
      config={config}
      useResizeHandler={true}
      style={{width: '100%', height: '100%'}}
    />
  );

};
